/**
 * WriterDrawer — bottom slide-up drawer for the office writing assistant.
 *
 * v5.16.2: optional props for pages that cover the bottom-left corner (the HealthSherpa page):
 *   placement='top'  -> the expanded panel opens from the TOP (same size and contents);
 *   hideTab          -> no collapsed bottom-left tab (the page shows its own Writer button);
 *   openRequest      -> a number; each change opens the panel.
 * Defaults ('bottom', false, 0) render exactly what earlier versions rendered.
 *
 * v3.4.1: agency selector required before any mode button enables.
 * Backend automatically appends signature with user's name + agency contact info.
 */
import React, { useState, useEffect, useRef } from 'react';
import { writer as writerApi } from './api.js';
import WriterSaveModal from './WriterSaveModal';

const MODES = [
  { id: 'polish', label: 'Polish' },
  { id: 'formalize', label: 'Formalize' },
  { id: 'friendly', label: 'Friendly' },
  { id: 'grammar', label: 'Grammar' },
  { id: 'insurance_letter', label: 'Insurance letter' },
  { id: 'shorten', label: 'Shorten' },
];

const AGENCIES = [
  { id: 'solutions', label: 'Solutions Insurance Agency' },
  { id: 'harry_dao', label: 'Harry Dao Insurance Agency' },
];

export default function WriterDrawer({ darkMode = true, placement = 'bottom', hideTab = false, openRequest = 0 }) {
  const [expanded, setExpanded] = useState(false);
  const atTop = placement === 'top';
  const [text, setText] = useState('');
  const [result, setResult] = useState('');
  const [activeMode, setActiveMode] = useState(null);
  const [agency, setAgency] = useState(null); // null until user picks
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(null);
  const [history, setHistory] = useState([]);
  const [copyLabel, setCopyLabel] = useState('Copy');
  const [showSave, setShowSave] = useState(false);
  const [translation, setTranslation] = useState('');
  const [translateLang, setTranslateLang] = useState(null);
  const [translateLoading, setTranslateLoading] = useState(false);
  const [translateError, setTranslateError] = useState('');
  const [translateCopyLabel, setTranslateCopyLabel] = useState('Copy');
  const textareaRef = useRef(null);

  const bg = darkMode ? '#1e1e1e' : '#ffffff';
  const bgSecondary = darkMode ? '#2a2a2a' : '#f7f7f7';
  const border = darkMode ? '#3a3a3a' : '#dddddd';
  const text1 = darkMode ? '#e0e0e0' : '#222222';
  const text2 = darkMode ? '#999999' : '#666666';
  const text3 = darkMode ? '#666666' : '#999999';
  const accent = '#185FA5';
  const accentBg = darkMode ? '#0F2A4A' : '#E6F1FB';
  const warningColor = '#A32D2D';

  useEffect(() => {
    if (expanded && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [expanded]);

  useEffect(() => {
    if (openRequest) setExpanded(true);     // v5.16.2: a page's own Writer button (0 = never)
  }, [openRequest]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  const canSubmit = text.trim() && agency && !loading;

  const handleRewrite = async (mode) => {
    if (!canSubmit) return;
    setActiveMode(mode);
    setLoading(true);
    setError('');
    setResult('');
    setElapsed(null);
    setTranslation('');
    setTranslateLang(null);
    setTranslateError('');
    try {
      const res = await writerApi.write({ text, mode, agency });
      const data = res.data;
      setResult(data.result);
      setElapsed(data.elapsed_seconds);
      setHistory((h) => [
        { mode, agency, input: text, output: data.result, ts: Date.now() },
        ...h.slice(0, 9),
      ]);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (txt = result) => {
    if (!txt) return;
    try {
      await navigator.clipboard.writeText(txt);
      setCopyLabel('Copied');
      setTimeout(() => setCopyLabel('Copy'), 1500);
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  const restoreFromHistory = (item) => {
    setText(item.input);
    setResult(item.output);
    setActiveMode(item.mode);
    if (item.agency) setAgency(item.agency);
    setElapsed(null);
  };

  const clearAll = () => {
    setText('');
    setResult('');
    setActiveMode(null);
    setError('');
    setElapsed(null);
    setTranslation('');
    setTranslateLang(null);
    setTranslateError('');
  };

  const handleTranslate = async (lang) => {
    if (!result || translateLoading) return;
    setTranslateLang(lang);
    setTranslateLoading(true);
    setTranslation('');
    setTranslateError('');
    try {
      const mode = lang === 'spanish' ? 'translate_spanish' : 'translate_vietnamese';
      const res = await writerApi.write({ text: result, mode, agency });
      setTranslation(res.data.result);
    } catch (e) {
      setTranslateError(e.response?.data?.detail || e.message || 'Translation failed');
    } finally {
      setTranslateLoading(false);
    }
  };

  const handleCopyTranslation = async () => {
    if (!translation) return;
    try {
      await navigator.clipboard.writeText(translation);
      setTranslateCopyLabel('Copied');
      setTimeout(() => setTranslateCopyLabel('Copy'), 1500);
    } catch {
      setTranslateError('Could not copy to clipboard');
    }
  };

  // ----- COLLAPSED TAB (bottom-left corner) -----
  if (!expanded) {
    if (hideTab) return null;               // v5.16.2: the page shows its own Writer button
    return (
      <div
        onClick={() => setExpanded(true)}
        onMouseEnter={(e) => { e.currentTarget.style.background = bgSecondary; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = bg; }}
        style={{
          position: 'fixed',
          left: '12px',
          bottom: 0,
          height: '40px',
          minWidth: '160px',
          padding: '0 16px',
          background: bg,
          border: `1px solid ${border}`,
          borderBottom: 'none',
          borderTopLeftRadius: '6px',
          borderTopRightRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          zIndex: 9000,
          userSelect: 'none',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          boxShadow: '0 -2px 6px rgba(0,0,0,0.15)',
          transition: 'background 0.12s',
        }}
        title="Click to open Writer"
      >
        <div style={{
          width: '20px', height: '20px', borderRadius: '50%',
          background: accentBg, display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '11px', fontWeight: 600, color: accent,
          flexShrink: 0,
        }}>W</div>
        <span style={{ fontSize: '14px', fontWeight: 500, color: text1, whiteSpace: 'nowrap' }}>Writer</span>
        <span style={{ fontSize: '13px', color: text2, marginLeft: '4px' }}>▲</span>
      </div>
    );
  }

  // ----- EXPANDED PANEL -----
  return (
    <>
      <div
        onClick={() => setExpanded(false)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9001 }}
      />
      <div style={{
        position: 'fixed', left: 0, right: 0, ...(atTop ? { top: 0 } : { bottom: 0 }),
        height: '75vh', maxHeight: '760px', background: bg,
        ...(atTop ? { borderBottom: `1px solid ${border}` } : { borderTop: `1px solid ${border}` }), zIndex: 9002,
        display: 'flex', flexDirection: 'column',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: text1, boxShadow: atTop ? '0 4px 24px rgba(0,0,0,0.3)' : '0 -4px 24px rgba(0,0,0,0.3)',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 16px', borderBottom: `1px solid ${border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{
              width: '18px', height: '18px', borderRadius: '50%',
              background: accentBg, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: '10px', fontWeight: 600, color: accent,
            }}>W</div>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>Writer</span>
            {elapsed !== null && (
              <span style={{ fontSize: '11px', color: text3, marginLeft: '8px' }}>
                Qwen3.6 · {elapsed}s
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {(text || result) && (
              <button onClick={clearAll} style={{
                fontSize: '12px', padding: '4px 10px', background: 'transparent',
                border: `1px solid ${border}`, borderRadius: '4px', color: text2, cursor: 'pointer',
              }}>Clear</button>
            )}
            <button onClick={() => setExpanded(false)} style={{
              fontSize: '14px', padding: '4px 10px', background: 'transparent',
              border: `1px solid ${border}`, borderRadius: '4px', color: text1, cursor: 'pointer',
            }} title="Collapse (Esc)">{atTop ? '▲' : '▼'}</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>

          {/* Main column */}
          <div style={{
            flex: 1, padding: '12px 16px', display: 'flex', flexDirection: 'column',
            gap: '10px', minWidth: 0,
          }}>

            {/* Agency selector */}
            <div style={{
              padding: '10px 12px', background: agency ? bgSecondary : (darkMode ? '#3a2a1a' : '#fff8e1'),
              border: `1px solid ${agency ? border : '#d4a017'}`, borderRadius: '4px',
            }}>
              <div style={{
                fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
                color: agency ? text3 : '#d4a017', marginBottom: '6px',
                fontWeight: agency ? 400 : 600,
              }}>
                Sign as {agency ? '✓' : '(required)'}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {AGENCIES.map((a) => (
                  <label key={a.id} style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '6px 10px', borderRadius: '4px', cursor: 'pointer',
                    background: agency === a.id ? accentBg : 'transparent',
                    border: `1px solid ${agency === a.id ? accent : border}`,
                    fontSize: '13px', color: agency === a.id ? accent : text1,
                  }}>
                    <input
                      type="radio"
                      name="agency"
                      value={a.id}
                      checked={agency === a.id}
                      onChange={() => setAgency(a.id)}
                      style={{ margin: 0 }}
                    />
                    {a.label}
                  </label>
                ))}
              </div>
            </div>

            {/* Input */}
            <div>
              <div style={{
                fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
                color: text3, marginBottom: '4px',
              }}>Your text</div>
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste an email or notes here, then pick a mode below..."
                style={{
                  width: '100%', minHeight: '90px', fontSize: '13px',
                  resize: 'vertical', boxSizing: 'border-box', padding: '8px 10px',
                  background: bgSecondary, border: `1px solid ${border}`,
                  borderRadius: '4px', color: text1, fontFamily: 'inherit',
                }}
              />
            </div>

            {/* Mode buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => handleRewrite(m.id)}
                  disabled={!canSubmit}
                  title={!agency ? 'Pick an agency above first' : !text.trim() ? 'Enter some text first' : ''}
                  style={{
                    fontSize: '12px', padding: '5px 11px', borderRadius: '4px',
                    cursor: !canSubmit ? 'not-allowed' : 'pointer',
                    background: activeMode === m.id ? accentBg : 'transparent',
                    border: `1px solid ${activeMode === m.id ? accent : border}`,
                    color: activeMode === m.id ? accent : text1,
                    opacity: !canSubmit ? 0.4 : 1,
                  }}
                >{m.label}</button>
              ))}
            </div>

            {/* Result */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: '4px',
              }}>
                <span style={{
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: text3,
                }}>Result</span>
                {result && (
                  <button onClick={() => setShowSave(true)} style={{
                    fontSize: '11px', padding: '3px 10px',
                    background: 'transparent',
                    border: `1px solid ${border}`,
                    borderRadius: '4px',
                    color: text1, cursor: 'pointer', marginRight: '6px',
                  }}>Save to client folder</button>
                )}
                {result && (
                  <button onClick={() => handleCopy()} style={{
                    fontSize: '11px', padding: '3px 10px',
                    background: copyLabel === 'Copied' ? accentBg : 'transparent',
                    border: `1px solid ${copyLabel === 'Copied' ? accent : border}`,
                    borderRadius: '4px',
                    color: copyLabel === 'Copied' ? accent : text1, cursor: 'pointer',
                  }}>{copyLabel}</button>
                )}
              </div>
              <div style={{
                background: bgSecondary, borderRadius: '4px', padding: '10px 12px',
                fontSize: '13px', lineHeight: 1.6, flex: 1, overflowY: 'auto',
                whiteSpace: 'pre-wrap', border: `1px solid ${border}`,
              }}>
                {loading && <span style={{ color: text3, fontStyle: 'italic' }}>Working...</span>}
                {error && <span style={{ color: warningColor }}>{error}</span>}
                {!loading && !error && !result && (
                  <span style={{ color: text3, fontStyle: 'italic' }}>
                    {agency ? 'Pick a mode above to rewrite your text. The result will appear here.'
                            : 'Pick which agency to sign as, then enter text and choose a mode.'}
                  </span>
                )}
                {!loading && !error && result}
              </div>
            </div>

            {/* Translation panel — only shows when there's an English result */}
            {result && (
              <div style={{
                marginTop: '10px', padding: '10px 12px',
                background: bgSecondary, borderRadius: '4px', border: `1px solid ${border}`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '12px', color: text2 }}>Translate to:</span>
                  <button
                    onClick={() => handleTranslate('spanish')}
                    disabled={translateLoading}
                    style={{
                      fontSize: '12px', padding: '4px 11px', borderRadius: '4px',
                      cursor: translateLoading ? 'not-allowed' : 'pointer',
                      background: translateLang === 'spanish' ? accentBg : 'transparent',
                      border: `1px solid ${translateLang === 'spanish' ? accent : border}`,
                      color: translateLang === 'spanish' ? accent : text1,
                      opacity: translateLoading ? 0.5 : 1,
                    }}
                  >Spanish</button>
                  <button
                    onClick={() => handleTranslate('vietnamese')}
                    disabled={translateLoading}
                    style={{
                      fontSize: '12px', padding: '4px 11px', borderRadius: '4px',
                      cursor: translateLoading ? 'not-allowed' : 'pointer',
                      background: translateLang === 'vietnamese' ? accentBg : 'transparent',
                      border: `1px solid ${translateLang === 'vietnamese' ? accent : border}`,
                      color: translateLang === 'vietnamese' ? accent : text1,
                      opacity: translateLoading ? 0.5 : 1,
                    }}
                  >Vietnamese</button>
                </div>

                {(translateLang || translateLoading || translation || translateError) && (
                  <>
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      marginTop: '10px', marginBottom: '4px',
                    }}>
                      <span style={{
                        fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: text3,
                      }}>{translateLang === 'spanish' ? 'Spanish' : translateLang === 'vietnamese' ? 'Vietnamese' : ''} translation</span>
                      {translation && (
                        <button onClick={handleCopyTranslation} style={{
                          fontSize: '11px', padding: '3px 10px',
                          background: translateCopyLabel === 'Copied' ? accentBg : 'transparent',
                          border: `1px solid ${translateCopyLabel === 'Copied' ? accent : border}`,
                          borderRadius: '4px',
                          color: translateCopyLabel === 'Copied' ? accent : text1, cursor: 'pointer',
                        }}>{translateCopyLabel}</button>
                      )}
                    </div>
                    <div style={{
                      background: bg, borderRadius: '4px', padding: '10px 12px',
                      fontSize: '13px', lineHeight: 1.6, whiteSpace: 'pre-wrap',
                      border: `1px solid ${border}`, maxHeight: '200px', overflowY: 'auto',
                    }}>
                      {translateLoading && <span style={{ color: text3, fontStyle: 'italic' }}>Translating...</span>}
                      {translateError && <span style={{ color: warningColor }}>{translateError}</span>}
                      {!translateLoading && !translateError && translation}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* History sidebar */}
          {history.length > 0 && (
            <div style={{
              width: '200px', borderLeft: `1px solid ${border}`,
              padding: '12px', overflowY: 'auto', display: 'flex',
              flexDirection: 'column', gap: '8px',
            }}>
              <div style={{
                fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: text3,
              }}>Session history</div>
              {history.map((item, idx) => {
                const modeLabel = MODES.find(m => m.id === item.mode)?.label || item.mode;
                return (
                  <div key={idx} onClick={() => restoreFromHistory(item)} style={{
                    padding: '8px', background: bgSecondary,
                    border: `1px solid ${border}`, borderRadius: '4px',
                    cursor: 'pointer', fontSize: '11px',
                  }}>
                    <div style={{ color: accent, marginBottom: '3px', fontWeight: 500 }}>
                      {modeLabel}
                    </div>
                    <div style={{
                      color: text2, overflow: 'hidden', textOverflow: 'ellipsis',
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    }}>
                      {item.input.slice(0, 80)}{item.input.length > 80 ? '…' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showSave && (
        <WriterSaveModal
          darkMode={darkMode}
          content={
            translation
              ? `${result}\n\n---\n\n${translateLang === 'spanish' ? 'Spanish translation' : 'Vietnamese translation'}\n\n${translation}`
              : result
          }
          suggestedTitle={activeMode === 'insurance_letter' ? 'Insurance Letter' : 'Letter'}
          onClose={() => setShowSave(false)}
          onSaved={() => { /* keep modal open to show success — user clicks Done */ }}
        />
      )}
    </>
  );
}
